import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, fetchJson } from "@/lib/queryClient";
import type {
  DetalheRomaneioDTO,
  FiltroRomaneios,
  NovoRomaneio,
  PatchRomaneio,
  RomaneiosDTO,
} from "@shared/mercado-romaneios";
import type { ProdutorCarteiraDTO } from "@shared/mercado-comprador";
import type { RomaneioPublicoDTO } from "@shared/mercado";

/**
 * Os ROMANEIOS do comprador — a caderneta dele.
 *
 * ⚠️ NENHUM CÁLCULO AQUI. KPIs, contagens dos chips e totais vêm prontos do
 * servidor; a tela só formata. Recontar no client criaria a segunda verdade
 * sobre a mesma compra — que é o que o teste de contrato (função ≡ rota) existe
 * para impedir.
 *
 * ⚠️ UMA consulta para a lista inteira. Quatro tiles + cinco chips + tabela em
 * requisições separadas seriam quatro esqueletos piscando em sequência no
 * celular, e quatro chances de a tela mostrar números de instantes diferentes.
 */

const ROMANEIOS = "/api/mercado/comprador/romaneios";
const CARGAS = "/api/mercado/comprador/cargas";
const PRODUTORES = "/api/mercado/comprador/produtores";
const INICIO = "/api/mercado/comprador/inicio";
const PAGAR = "/api/mercado/comprador/pagar";

/**
 * Um romaneio mexe em tudo que soma sobre ele: a lista, o detalhe, a carga em
 * que ele está (ou deixou de estar), o que se deve e o painel do Início.
 * Invalidar menos que isso é deixar duas telas do mesmo app discordando.
 */
function invalidarRomaneios(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [ROMANEIOS] });
  qc.invalidateQueries({ queryKey: [CARGAS] });
  qc.invalidateQueries({ queryKey: [PRODUTORES] });
  qc.invalidateQueries({ queryKey: [PAGAR] });
  qc.invalidateQueries({ queryKey: [INICIO] });
}

/** O filtro vira query string — as MESMAS chaves que a URL da tela usa. */
function queryString(filtro: FiltroRomaneios): string {
  const p = new URLSearchParams();
  if (filtro.periodo && filtro.periodo !== "mes") p.set("periodo", filtro.periodo);
  if (filtro.de) p.set("de", filtro.de);
  if (filtro.ate) p.set("ate", filtro.ate);
  if (filtro.produtorContatoId) p.set("produtor", filtro.produtorContatoId);
  if (filtro.origem) p.set("origem", filtro.origem);
  if (filtro.situacao) p.set("situacao", filtro.situacao);
  if (filtro.q) p.set("q", filtro.q);
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

export function useRomaneios(filtro: FiltroRomaneios, ativo: boolean) {
  const qs = queryString(filtro);
  return useQuery<RomaneiosDTO>({
    // A query string inteira na chave: dois recortes diferentes são duas
    // respostas diferentes, e cachear os dois sob a mesma chave faria a tela
    // piscar o recorte anterior a cada troca de filtro.
    queryKey: [ROMANEIOS, "lista", qs],
    queryFn: () => fetchJson<RomaneiosDTO>(`${ROMANEIOS}${qs}`),
    enabled: ativo,
  });
}

export function useRomaneio(ref: string | undefined) {
  return useQuery<DetalheRomaneioDTO>({
    queryKey: [ROMANEIOS, "detalhe", ref],
    queryFn: () => fetchJson<DetalheRomaneioDTO>(`${ROMANEIOS}/${encodeURIComponent(ref!)}`),
    enabled: !!ref,
  });
}

/**
 * O DOCUMENTO do produtor — só do romaneio do Rizoma, e só quando o formulário
 * de conferência precisa dele.
 *
 * ⚠️ Consulta SEPARADA do detalhe de propósito: é a nota item a item como o
 * produtor a emitiu, e só interessa a quem vai conferir. Trazê-la junto do
 * detalhe custaria a leitura da venda inteira em toda abertura do painel.
 */
export function useDocumentoDoRomaneio(ref: string | undefined, ativo: boolean) {
  return useQuery<RomaneioPublicoDTO & { compartilhamentoId: string }>({
    queryKey: [ROMANEIOS, "documento", ref],
    queryFn: () => fetchJson<RomaneioPublicoDTO & { compartilhamentoId: string }>(
      `${ROMANEIOS}/${encodeURIComponent(ref!)}/documento`,
    ),
    enabled: ativo && !!ref && ref.startsWith("c:"),
  });
}

/** A carteira: os chips de produtor do formulário e o select de filtro da lista. */
export function useCarteira(ativo: boolean) {
  return useQuery<ProdutorCarteiraDTO[]>({
    queryKey: [PRODUTORES],
    queryFn: () => fetchJson<ProdutorCarteiraDTO[]>(PRODUTORES),
    enabled: ativo,
  });
}

/**
 * Registrar: gravou, OU há contato parecido e a tela precisa PERGUNTAR.
 *
 * ⚠️ Este é o único hook do módulo que não usa `apiRequest`, e é de propósito:
 * `throwIfResNotOk` transforma o 409 numa `Error` com o JSON dentro da string, e
 * a tela precisa do `sugestao` PARSEADO para montar o diálogo "É ele / É outro".
 * Regex sobre mensagem de erro para recuperar dado estruturado é como se perde a
 * sugestão no primeiro campo novo.
 */
export type ResultadoRegistro =
  | { ok: true; romaneio: DetalheRomaneioDTO }
  | { ok: false; parecidos: ProdutorCarteiraDTO[]; mensagem: string };

export function useRegistrarRomaneio() {
  const qc = useQueryClient();
  return useMutation<ResultadoRegistro, Error, NovoRomaneio>({
    mutationFn: async (dados) => {
      const res = await fetch(ROMANEIOS, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dados),
        credentials: "include",
      });
      if (res.ok) return { ok: true, romaneio: (await res.json()) as DetalheRomaneioDTO };

      const corpo = await res.json().catch(() => null) as
        { code?: string; error?: string; sugestao?: ProdutorCarteiraDTO[] } | null;
      if (res.status === 409 && corpo?.code === "PRODUTOR_PARECIDO") {
        return { ok: false, parecidos: corpo.sugestao ?? [], mensagem: corpo.error ?? "" };
      }
      // Qualquer outra falha é erro de verdade e sobe — nunca engolida num
      // `catch {}`, que deixaria o corretor achando que o romaneio foi gravado.
      throw new Error(corpo?.error ?? `${res.status}: não consegui registrar o romaneio.`);
    },
    onSuccess: (r) => { if (r.ok) invalidarRomaneios(qc); },
  });
}

export function useEditarRomaneio(ref: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: PatchRomaneio) =>
      apiRequest("PATCH", `${ROMANEIOS}/${encodeURIComponent(ref!)}`, dados)
        .then(r => r.json()) as Promise<DetalheRomaneioDTO>,
    onSuccess: () => invalidarRomaneios(qc),
  });
}

export function useExcluirRomaneio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ref: string) =>
      apiRequest("DELETE", `${ROMANEIOS}/${encodeURIComponent(ref)}`).then(r => r.json()),
    onSuccess: () => invalidarRomaneios(qc),
  });
}

export function useIncluirEmCarga() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { refs: string[]; cargaId: string }) =>
      apiRequest("POST", `${ROMANEIOS}/incluir-em-carga`, dados)
        .then(r => r.json()) as Promise<{ incluidos: number; cargaId: string; identificador: string }>,
    onSuccess: () => invalidarRomaneios(qc),
  });
}

export function useTirarDaCarga() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ref: string) =>
      apiRequest("POST", `${ROMANEIOS}/${encodeURIComponent(ref)}/tirar-da-carga`)
        .then(r => r.json()) as Promise<DetalheRomaneioDTO>,
    onSuccess: () => invalidarRomaneios(qc),
  });
}

/** Cria a carga JÁ com os romaneios selecionados — a "Montar nova carga" da barra. */
export function useCargaComRomaneios() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: {
      refs: string[];
      carga: { variedade?: string | null; dataCarregamento?: string | null; alvoKg?: number | null; destinoContatoId?: string | null };
    }) => apiRequest("POST", `${CARGAS}/com-romaneios`, dados)
      .then(r => r.json()) as Promise<{ cargaId: string; identificador: string; incluidos: number }>,
    onSuccess: () => invalidarRomaneios(qc),
  });
}

/** As cargas EM FORMAÇÃO — o destino possível de um romaneio sem carga. */
export function useCargasEmFormacao(ativo: boolean) {
  return useQuery<{ id: string; identificador: string; alvoKg: number | null; faltam: number | null }[]>({
    queryKey: [CARGAS, "lista", "andamento"],
    queryFn: async () => {
      const linhas = await fetchJson<Array<{
        id: string; identificador: string; status: string; alvoKg: number | null;
        resumo: { faltam: number | null };
      }>>(`${CARGAS}?estado=andamento`);
      return linhas
        .filter(c => c.status === "EM_FORMACAO")
        .map(c => ({ id: c.id, identificador: c.identificador, alvoKg: c.alvoKg, faltam: c.resumo.faltam }));
    },
    enabled: ativo,
  });
}
